import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HttpError } from '@api/http';
import { useAuth } from '@auth/AuthContext';

export default function LoginPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!auth.isLoading && auth.isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-10">
      <form
        className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-card"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          auth
            .login({ email, password })
            .then(() => navigate(from, { replace: true }))
            .catch((err: unknown) => {
              const message =
                err instanceof HttpError
                  ? err.message
                  : err instanceof Error
                    ? err.message
                    : 'Login failed';
              setError(message);
            })
            .finally(() => setBusy(false));
        }}
      >
        <h1 className="text-xl font-semibold text-foreground">Sign in</h1>
        <p className="mt-1 text-sm text-muted">Use your NVR account credentials.</p>

        <div className="mt-5 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-muted">Email</span>
            <input
              className="w-full rounded-md border border-border bg-bg px-3 py-2 outline-none focus:border-accent"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted">Password</span>
            <input
              className="w-full rounded-md border border-border bg-bg px-3 py-2 outline-none focus:border-accent"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
